// useHasAddon — client-side entitlement read for a paid marketplace add-on.
//
// SOFT gate, UI only. This decides whether to SHOW an add-on affordance (a
// "Scan details" button, a premium map layer, …). The REAL authorization is the
// fail-closed has_active_addon() check inside each add-on's gateway edge
// function — never rely on this hook to protect a billable call or guard data.
//
// Shares the ['tenant-addons', tenantId] query key + column shape with the
// Marketplace page (src/modules/shared/Addons.tsx) so the two read one cached
// row set and can't diverge. See src/lib/addons.ts + docs/plans/ADDONS-MARKETPLACE.md.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'

interface TenantAddonRow { addon_key: string; status: string; current_period_end: string | null; price_zar: number | null }

export interface AddonState {
  /** True only on a CONFIRMED active entitlement. */
  enabled:  boolean
  /** True until the entitlement read has settled (pending or paused). */
  unknown:  boolean
  /** The read failed — show an error, never the upsell. */
  isError:  boolean
  refetch:  () => void
}

function addonUnknown(tenantId: string | null, q: { isPending: boolean; isPaused: boolean }): boolean {
  if (!tenantId) return true
  return q.isPending || q.isPaused
}

// State-aware variant. The boolean hook below collapses pending AND error to
// `false`, which is fine for hiding a bonus button — but a page that swaps
// its ENTIRE body to a purchase upsell on `false` (SIZA) turns a flaky fetch
// into "buy this add-on" in front of an auditor (W2 §3.3). Gate whole-page
// entitlement swaps on this instead: upsell only when settled AND !enabled.
export function useAddonState(addonKey: string): AddonState {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const q = useQuery({
    queryKey: ['tenant-addons', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('tenant_addons')
        .select('addon_key, status, current_period_end, price_zar')
        .eq('tenant_id', tenantId)   // RLS already scopes this; explicit for platform-admin safety
      if (error) throw error
      return (data ?? []) as unknown as TenantAddonRow[]
    },
  })
  const rows = q.data ?? []
  // A plan can include an add-on outright (plan_tiers.included_addons — both
  // plans carry compliance_pro); has_active_addon() answers the same on the server.
  const fromPlan = (tenant?.planIncludedAddons ?? []).includes(addonKey)
  return {
    enabled: fromPlan || rows.some(r => r.addon_key === addonKey && r.status === 'ACTIVE'),
    unknown: addonUnknown(tenantId, q),
    isError: q.isError,
    refetch: () => { q.refetch() },
  }
}

export function useHasAddon(addonKey: string): boolean {
  return useAddonState(addonKey).enabled
}
